import React from 'react';
import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChangeOptions,
} from 'downshift';
import styled, { css, useTheme } from 'styled-components/macro';
import { useTranslate } from '@federico.moretti/react-translate';
import { Text } from '../../typography';
import { SPACINGS, FlexWrapper } from '../../utils/style';
import {
  IconChevron,
  IconQadra,
  IconLoader,
  IconWrapper,
  IconX,
} from '../../icons';
import { useDebounce } from '../../utils/hooks';
import { Checkbox } from '../../atoms';
import { replaceValues } from '../../utils/strings';
import { useInstrumentProperties } from '../../utils/hooks';

type InstrumentType = 'bond' | 'equity' | 'etf' | 'fund' | 'mandate' | 'cash';
const allInstrumentTypes: InstrumentType[] = [
  'bond',
  'equity',
  'etf',
  'fund',
  'mandate',
  'cash'
];

const AdvancedInstrumentSearchWrapper = styled.div<SelectStyledType>`
  position: relative;
  height: 38px; /* change to SPACING[10] */
  width: auto;

  border: 1px solid ${(p) => p.theme.colors.secondary80w};
  border-radius: ${(p) => (p.isMenuOpen ? '10px 10px 0 0' : '10px')};
  background-color: ${(p) =>
    p.disabled ? p.theme.colors.secondary90w : 'transparent'};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};

  ${(p) =>
    p.isMenuOpen &&
    css`
      box-shadow: 0 0 0 2px ${p.theme.colors.white},
        0 0 0 3px ${p.theme.colors.primary};
    `}

  ${(p) =>
    p.isFocused &&
    css`
      box-shadow: 0 0 0 2px ${p.theme.colors.white},
        0 0 0 3px ${p.theme.colors.primary};
    `}
`;

type SelectStyledType = {
  isMenuOpen: boolean;
  disabled: boolean;
  isFocused: boolean;
};
const InputStyled = styled.input<SelectStyledType>`
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0 ${SPACINGS[4]};
  margin: 0;
  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-size: 1rem;
`;

const IconType = styled.div<{ type: string }>`
  width: 50px;
  background-color: ${(p) => p.theme.colors[p.type]};
  border-radius: 5px;
`;

const MenuWrapper = styled.ul`
  width: auto;
  margin: 0;
  border-top: 0;
  padding: 0;
  margin-top: 2px;
  position: absolute;
  left: 0;
  right: 0;
  z-index: 5;
`;

const MenuStyled = styled.div`
  display: flex;
  flex-direction: column;
  outline: none;

  padding: 0;
  margin: 0;
  width: 100%;
  background-color: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.secondary80w};
  border-radius: 0 0
    ${(p) =>
    `${p.theme.dimensions.borderRadius} ${p.theme.dimensions.borderRadius}`};
  z-index: ${(p) => p.theme.zIndexes.overlay};

  box-shadow: 0 0 0 2px ${(p) => p.theme.colors.white},
    0 0 0 3px ${(p) => p.theme.colors.primary};
`;

const TableWrapper = styled.div`
  max-height: 14em;
  overflow: auto;
`;

const Table = styled.table`
  padding: 0 ${SPACINGS[4]};
  border-collapse: collapse;
  width: 100%;
`;

const ItemRowStyled = styled.tr<{ highlighted: boolean }>`
  list-style: none;
  background-color: ${(p) =>
    p.highlighted ? p.theme.colors.primary80w : p.theme.colors.white};
`;

const ItemCellStyled = styled.td`
  padding: ${SPACINGS[1]};
  padding-right: ${SPACINGS[8]};
  border: none;
`;

const MenuIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: ${SPACINGS[2]};
  top: 0;
  height: 38px; /* change to SPACING[10] */
  border: none;
`;

type DataState = 'loaded' | 'loading';

type AdvancedInstrumentSearchProps = {
  api: (search: string, params: any) => Promise<any[]>;
  onItemChange: (i: any) => void;
  itemToString: (i: any) => string;
  typesToExclude?: InstrumentType[];
  minimumInputChars?: number;
  defaultInputValue?: string;
  defaultRecommended?: boolean;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  testid?: string;
};
function AdvancedInstrumentSearch(props: AdvancedInstrumentSearchProps) {
  const { t } = useTranslate();
  const {
    api,
    onItemChange,
    itemToString,
    disabled,
    typesToExclude = [],
    minimumInputChars = 4,
    defaultInputValue = '',
    defaultRecommended,
    style,
    placeholder = t('assetSearchPlaceholder'),
    testid,
  } = props;

  const theme = useTheme();
  const getInstrumentProperties = useInstrumentProperties();

  const [data, setData] = React.useState([] as any[]);
  const [dataState, setDataState] = React.useState<DataState>('loaded');

  const inputRef = React.useRef<HTMLInputElement>(null);
  const isInputFocused = () => document.activeElement === inputRef.current;

  function stateReducer(
    _: UseComboboxState<any>,
    { type, changes }: UseComboboxStateChangeOptions<any>
  ) {
    switch (type) {
      case useCombobox.stateChangeTypes.ItemClick:
        return {
          ...changes,
          inputValue: '',
        };
      case useCombobox.stateChangeTypes.FunctionSelectItem:
        return {
          ...changes,
          inputValue: '',
        };
      default:
        return changes;
    }
  }

  const {
    isOpen,
    getComboboxProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    getInputProps,
    toggleMenu,
    openMenu,
    setInputValue,
  } = useCombobox({
    items: data,
    itemToString: itemToString,
    stateReducer,
    defaultInputValue: defaultInputValue,
    onSelectedItemChange: (changes) => {
      onItemChange(changes.selectedItem);
      setInputValue('');
    },
  });

  const debouncedInputValue = useDebounce(inputValue, 500);

  const instrumentTypes: InstrumentType[] = allInstrumentTypes.filter(
    (t) => !typesToExclude.includes(t)
  );

  const [queryInstrumentTypes, setQueryInstrumentTypes] = React.useState<
    InstrumentType[]
  >(instrumentTypes);

  const [queryRecommended, setQueryRecommended] = React.useState(
    Boolean(defaultRecommended)
  );

  React.useEffect(() => {
    let ignore = false;

    async function fetchApi(value: string) {
      const res = await api(value, {
        types: queryInstrumentTypes,
        recommended: queryRecommended ? true : undefined,
      });
      if (!ignore) {
        setData(res);
        setDataState('loaded');
      }
    }

    if (debouncedInputValue.length >= minimumInputChars) {
      setDataState('loading');
      fetchApi(debouncedInputValue);
    }

    return () => {
      ignore = true;
    };
  }, [
    api,
    debouncedInputValue,
    minimumInputChars,
    queryInstrumentTypes,
    queryRecommended,
  ]);

  const handleCheckbox = React.useCallback(
    (type: InstrumentType, checked: boolean) => {
      setQueryInstrumentTypes((v) => {
        if (checked) {
          const s = new Set([...v, type]);
          return Array.from(s);
        }
        return v.filter((t) => t !== type);
      });
      if (!isOpen) openMenu();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, openMenu]
  );

  const buttons = (
    <FlexWrapper style={{ padding: SPACINGS[4] }}>
      <FlexWrapper style={{ width: 'auto' }}>
        {instrumentTypes.map((type) => (
          <React.Fragment key={type}>
            <Checkbox
              key={type}
              name={type}
              checked={queryInstrumentTypes.includes(type)}
              onChange={(e) => handleCheckbox(type, e.target.checked)}
            />
            <Text
              htmlFor={type}
              type="label"
              size="sm"
              familyType="sans"
              margin="none"
              style={{
                color: theme.colors.black,
                marginLeft: SPACINGS[2],
                marginRight: SPACINGS[6],
              }}
            >
              {t(type)}
            </Text>
          </React.Fragment>
        ))}
      </FlexWrapper>
      <FlexWrapper style={{ width: 'auto' }}>
        <Checkbox
          name="recommended"
          checked={queryRecommended}
          onChange={(e) => {
            if (!isOpen) openMenu();
            setQueryRecommended(e.target.checked);
          }}
        />
        <FlexWrapper style={{ width: 'auto' }} justify="flex-start">
          <Text
            htmlFor="recommended"
            type="label"
            size="sm"
            familyType="sans"
            margin="none"
            style={{
              color: theme.colors.black,
              marginLeft: SPACINGS[2],
            }}
          >
            {t('recommendedAssets')}
          </Text>
          <IconQadra />
        </FlexWrapper>
      </FlexWrapper>
    </FlexWrapper>
  );

  const noResultsText =
    inputValue.length < minimumInputChars
      ? replaceValues(
        `${t('noResults')} - ${t('typeToSearch').toLowerCase()}`,
        [minimumInputChars]
      )
      : t('noResults');

  return (
    <div style={{ ...style, position: 'relative' }}>
      <AdvancedInstrumentSearchWrapper
        isMenuOpen={isOpen}
        disabled={Boolean(disabled)}
        isFocused={isInputFocused()}
        {...getComboboxProps()}
      >
        <InputStyled
          data-testid={`${testid}-input`}
          placeholder={placeholder}
          onClick={openMenu}
          isMenuOpen={isOpen}
          disabled={Boolean(disabled)}
          {...getInputProps({ ref: inputRef, disabled })}
        />
        <MenuIcon>
          {dataState === 'loading' && (
            <IconWrapper style={{ marginRight: SPACINGS[2] }}>
              <IconLoader stroke={theme.colors.primary20b} />
            </IconWrapper>
          )}
          {inputValue.length > 0 && (
            <IconWrapper
              style={{ cursor: 'pointer' }}
              onClick={() => setInputValue('')}
              backgroundColor={theme.colors.transparent}
              hoverColor={theme.colors.transparent  }
            >
              <IconX stroke={theme.colors.primary20b} />
            </IconWrapper>
          )}
          {isOpen ? (
            <IconWrapper
              data-testid={`${testid}-menu-button`}
              style={{ cursor: 'pointer' }}
              onClick={toggleMenu}
              backgroundColor={theme.colors.transparent}
              hoverColor={theme.colors.transparent  }
            >
              <IconChevron stroke={theme.colors.primary20b} direction="top" />
            </IconWrapper>
          ) : (
            <IconWrapper
              data-testid={`${testid}-menu-button`}
              style={{ cursor: 'pointer' }}
              onClick={toggleMenu}
              backgroundColor={theme.colors.transparent}
              hoverColor={theme.colors.transparent  }
            >
              <IconChevron
                stroke={theme.colors.primary20b}
                direction="bottom"
              />
            </IconWrapper>
          )}
        </MenuIcon>
      </AdvancedInstrumentSearchWrapper>
      <MenuWrapper {...getMenuProps()}>
        {isOpen && (
          <MenuStyled>
            {buttons}
            {data.length > 0 ? (
              <TableWrapper>
                <Table>
                  <tbody>
                    <tr>
                      <th></th>
                      <th align="left">
                        <Text size="sm" type="label">
                          {t('name')}
                        </Text>
                      </th>
                      <th align="left">
                        <Text size="sm" type="label">
                          {t('isin')}
                        </Text>
                      </th>
                      <th></th>
                    </tr>
                    {data.map((item, index) => (
                      <ItemRowStyled
                        data-testid={`${testid}-item-${index}`}
                        highlighted={highlightedIndex === index}
                        key={`${itemToString(item)}${index}`}
                        {...getItemProps({ item, index })}
                      >
                        <ItemCellStyled
                          style={{
                            width: 1,
                            paddingLeft: SPACINGS[4],
                            paddingRight: SPACINGS[2],
                          }}
                        >
                          <IconType type={item?.productType.toLowerCase()}>
                            <Text
                              display="block"
                              margin="none"
                              align="center"
                              size="sm"
                              transform="uppercase"
                            >
                              {
                                getInstrumentProperties(
                                  item?.productType.toLowerCase()
                                ).abbreviation
                              }
                            </Text>
                          </IconType>
                        </ItemCellStyled>
                        <ItemCellStyled>
                          <Text margin="none">{item.name}</Text>
                        </ItemCellStyled>
                        <ItemCellStyled>
                          <Text margin="none">{item.isin}</Text>
                        </ItemCellStyled>
                        <ItemCellStyled>
                          <Text margin="none">
                            {item.recommended ? <IconQadra /> : ''}
                          </Text>
                        </ItemCellStyled>
                      </ItemRowStyled>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : (
              <Text opacity={50} style={{ paddingLeft: SPACINGS[4] }}>
                {noResultsText}
              </Text>
            )}
          </MenuStyled>
        )}
      </MenuWrapper>
      <div style={{ paddingTop: 3, paddingLeft: 1 }}>{buttons}</div>
    </div>
  );
}

export default React.memo(
  AdvancedInstrumentSearch
) as typeof AdvancedInstrumentSearch;
